import { LOCATION_TYPE_LABELS, LOCATION_TYPES, type LocationType } from '@wms/domain';
import { useMemo, useState } from 'react';
import { api, errorMessage } from '../../../api';
import { decimalInput, fmt } from '../../../format';
import type { LocationView, WarehouseView } from '../../../types';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../../ui';
import { Icon } from '../../../ui/icons';
import { useSort } from '../../../ui/sort';

const SHOWN = 1000;

interface PatchResult {
  readonly updated: number;
  readonly refused: readonly { readonly code: string; readonly reason: string }[];
}

/** The bins of the warehouse with filters; selected bins are edited together or sent to the label printer. */
export function Locations({ warehouse, onChanged, onPrint }: { warehouse: WarehouseView; onChanged: () => void; onPrint: (codes: readonly string[]) => void }) {
  const toast = useToast();
  const [aisle, setAisle] = useState('');
  const [type, setType] = useState<LocationType | ''>('');
  const [active, setActive] = useState<'' | 'true' | 'false'>('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [refused, setRefused] = useState<PatchResult['refused']>([]);
  const [editing, setEditing] = useState<'capacity' | 'type' | null>(null);
  const all = useLoad(() => api<LocationView[]>('GET', `/api/admin/warehouses/${warehouse.id}/locations`), [warehouse.id]);
  const aisles = useMemo(() => [...new Set((all.data ?? []).map((l) => l.aisle))], [all.data]);
  const sort = useSort<LocationView>('locations', {
    code: { label: 'Göz', value: (l) => l.code },
    barcode: { label: 'Barkod', value: (l) => l.barcode },
    type: { label: 'Tür', value: (l) => LOCATION_TYPES.indexOf(l.type) },
    position: { label: 'Konum (m)', value: (l) => l.x },
    volume: { label: 'Hacim', value: (l) => l.maxVolumeCm3, desc: true },
    weight: { label: 'Taşıma', value: (l) => l.maxWeightG, desc: true },
    stock: { label: 'Stok', value: (l) => l.onHand, desc: true },
    // passive and locked bins first either way
    state: { label: 'Durum', value: (l) => [!l.isActive && 'Pasif', l.isBlocked && 'Sayım kilidi'].filter(Boolean).join(' ') },
  });
  const term = search.trim().toUpperCase();
  const rows = sort.apply(
    (all.data ?? []).filter(
      (l) => (!aisle || l.aisle === aisle) && (!type || l.type === type) && (!active || String(l.isActive) === active) && (!term || l.code.includes(term) || l.barcode.includes(term)),
    ),
  );
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.code));

  const patch = async (body: Record<string, unknown>, done: string) => {
    try {
      const r = await api<PatchResult>('PATCH', `/api/admin/warehouses/${warehouse.id}/locations`, { codes: [...selected], patch: body });
      setRefused(r.refused);
      toast(`${r.updated} göz ${done}${r.refused.length ? `, ${r.refused.length} göz değişmedi` : ''}`, r.refused.length ? 'info' : 'ok');
      setEditing(null);
      void all.reload();
      onChanged();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  return (
    <>
      <div className="row">
        <select className="select" style={{ maxWidth: 160 }} value={aisle} onChange={(e) => setAisle(e.target.value)} aria-label="Koridor">
          <option value="">Tüm koridorlar</option>
          {aisles.map((a) => (
            <option key={a} value={a}>
              {a} koridoru
            </option>
          ))}
        </select>
        <select className="select" style={{ maxWidth: 200 }} value={type} onChange={(e) => setType(e.target.value as LocationType | '')} aria-label="Tür">
          <option value="">Tüm türler</option>
          {LOCATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {LOCATION_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <select className="select" style={{ maxWidth: 150 }} value={active} onChange={(e) => setActive(e.target.value as '' | 'true' | 'false')} aria-label="Durum">
          <option value="">Aktif ve pasif</option>
          <option value="true">Aktif</option>
          <option value="false">Pasif</option>
        </select>
        <input className="input" style={{ maxWidth: 200 }} placeholder="Göz kodu veya barkod" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="muted">{fmt.n(rows.length)} göz</span>
      </div>
      {selected.size > 0 && (
        <div className="row selection-bar">
          <b>{selected.size} göz seçili</b>
          <button className="btn sm primary" onClick={() => onPrint([...selected])}>
            <Icon name="printer" size={16} /> Etiket bas
          </button>
          <button className="btn sm" onClick={() => setEditing('capacity')}>
            Kapasite
          </button>
          <button className="btn sm" onClick={() => setEditing('type')}>
            Tür
          </button>
          <button className="btn sm" onClick={() => void patch({ isActive: false }, 'pasife alındı')}>
            Pasife al
          </button>
          <button className="btn sm" onClick={() => void patch({ isActive: true }, 'aktifleştirildi')}>
            Aktifleştir
          </button>
          <button className="btn sm ghost" onClick={() => setSelected(new Set())}>
            Seçimi kaldır
          </button>
        </div>
      )}
      {refused.length > 0 && (
        <div className="banner warn">
          <div>
            Değişmeyen gözler:
            <div style={{ fontWeight: 500 }}>{refused.slice(0, 12).map((r) => `${r.code}: ${r.reason}`).join(' · ')}</div>
          </div>
        </div>
      )}
      <section className="card">
        {all.error && <ErrorBanner message={all.error} onRetry={() => void all.reload()} />}
        {!all.data ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty title="Göz yok">"Toplu göz oluştur" ile koridor, raf, kat ve gözleri tanımlayın.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      aria-label="Hepsini seç"
                      checked={allSelected}
                      onChange={(e) => setSelected(e.target.checked ? new Set([...selected, ...rows.map((r) => r.code)]) : new Set([...selected].filter((c) => !rows.some((r) => r.code === c))))}
                    />
                  </th>
                  {sort.th('code')}
                  {sort.th('barcode')}
                  {sort.th('type')}
                  {sort.th('position')}
                  {sort.th('volume', 'num')}
                  {sort.th('weight', 'num')}
                  {sort.th('stock', 'num')}
                  {sort.th('state')}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, SHOWN).map((l) => (
                  <tr key={l.id} className={!l.isActive ? 'muted-row' : selected.has(l.code) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${l.code} seç`}
                        checked={selected.has(l.code)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(l.code);
                          else next.delete(l.code);
                          setSelected(next);
                        }}
                      />
                    </td>
                    <td className="mono">
                      <b>{l.code}</b>
                    </td>
                    <td className="mono muted">{l.barcode}</td>
                    <td>{LOCATION_TYPE_LABELS[l.type]}</td>
                    <td className="mono muted">{l.x === null ? '—' : `${fmt.dec(l.x)} · ${fmt.dec(l.y ?? 0)} · ${fmt.dec(l.z ?? 0)}`}</td>
                    <td className="num">{l.maxVolumeCm3 === null ? '—' : `${fmt.dec(l.maxVolumeCm3 / 1000)} L`}</td>
                    <td className="num">{l.maxWeightG === null ? '—' : `${fmt.dec(l.maxWeightG / 1000)} kg`}</td>
                    <td className="num">{l.onHand > 0 ? fmt.n(l.onHand) : '—'}</td>
                    <td>
                      {!l.isActive && <span className="chip">Pasif</span>} {l.isBlocked && <span className="chip bad">Sayım kilidi</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > SHOWN && <div className="muted" style={{ padding: 12 }}>İlk {SHOWN} göz gösteriliyor; koridor seçerek daraltın.</div>}
          </div>
        )}
      </section>
      {editing === 'capacity' && <CapacityDialog count={selected.size} onClose={() => setEditing(null)} onSave={(body) => void patch(body, 'güncellendi')} />}
      {editing === 'type' && <TypeDialog count={selected.size} onClose={() => setEditing(null)} onSave={(t) => void patch({ type: t }, 'güncellendi')} />}
    </>
  );
}

function CapacityDialog({ count, onClose, onSave }: { count: number; onClose: () => void; onSave: (body: Record<string, number | null>) => void }) {
  const [litres, setLitres] = useState('');
  const [kg, setKg] = useState('');
  const l = litres.trim() ? decimalInput(litres) : null;
  const k = kg.trim() ? decimalInput(kg) : null;
  const ok = (litres.trim() === '' || l !== null) && (kg.trim() === '' || k !== null) && (litres.trim() !== '' || kg.trim() !== '');
  return (
    <Modal
      title={`${count} gözün kapasitesi`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button
            className="btn primary"
            disabled={!ok}
            onClick={() =>
              onSave({
                ...(l !== null ? { maxVolumeCm3: Math.round(Number(l) * 1000) } : {}),
                ...(k !== null ? { maxWeightG: Math.round(Number(k) * 1000) } : {}),
              })
            }
          >
            Kaydet
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label htmlFor="cap-l">Göz hacmi (litre)</label>
          <input id="cap-l" className="input" inputMode="decimal" value={litres} onChange={(e) => setLitres(e.target.value)} placeholder="60" />
          <span className="hint">Yerleştirme önerisi ve doluluk renkleri bu hacme göre hesaplanır</span>
        </div>
        <div className="field">
          <label htmlFor="cap-kg">Taşıma kapasitesi (kg)</label>
          <input id="cap-kg" className="input" inputMode="decimal" value={kg} onChange={(e) => setKg(e.target.value)} placeholder="40" />
        </div>
      </div>
    </Modal>
  );
}

function TypeDialog({ count, onClose, onSave }: { count: number; onClose: () => void; onSave: (type: LocationType) => void }) {
  const [type, setType] = useState<LocationType>('pick');
  return (
    <Modal
      title={`${count} gözün türü`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" onClick={() => onSave(type)}>
            Kaydet
          </button>
        </>
      }
    >
      <div className="stack">
        <select className="select" value={type} onChange={(e) => setType(e.target.value as LocationType)}>
          {LOCATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {LOCATION_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <p className="muted" style={{ fontSize: 13 }}>
          İçinde stok olan gözlerin türü değişmez. Toplama ve yedek alan gözlerindeki stok siparişlere ayrılabilir; kabul, iade, karantina ve
          hasarlı alanlarındaki stok ayrılmaz.
        </p>
      </div>
    </Modal>
  );
}
